/**
 * Scroll Animations Component
 *
 * Animates elements into view based on data attributes.
 * Supports multiple animation types, customizable speed, and scroll scrubbing.
 *
 * Usage:
 * <div data-scroll-animate="fade" data-animate-duration="1" data-animate-scrub="false">
 *   Content to animate
 * </div>
 *
 * Available Animations:
 * - fade: Fade in
 * - fadeUp: Fade in from below
 * - fadeDown: Fade in from above
 * - fadeLeft: Fade in from left
 * - fadeRight: Fade in from right
 * - slideUp: Slide up from below
 * - slideDown: Slide down from above
 * - slideLeft: Slide in from left
 * - slideRight: Slide in from right
 * - scale: Scale up from small
 * - scaleDown: Scale down from large
 *
 * Data Attributes:
 * - data-scroll-animate: Animation type (required)
 * - data-animate-duration: Animation duration in seconds (default: 0.8)
 * - data-animate-scrub: Enable scroll scrubbing (default: false)
 * - data-animate-delay: Delay before animation starts in seconds (default: 0)
 * - data-animate-ease: GSAP easing function (default: "power2.out")
 * - data-animate-start: ScrollTrigger start position (default: "top 80%")
 * - data-animate-stagger: Stagger delay for child elements (optional)
 */

import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

// Register GSAP plugin
gsap.registerPlugin(ScrollTrigger);

/**
 * Animation presets
 * Each preset defines the initial state (from) and final state (to)
 */
const ANIMATION_PRESETS = {
  fade: {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  fadeUp: {
    from: { opacity: 0, y: 50 },
    to: { opacity: 1, y: 0 },
  },
  fadeDown: {
    from: { opacity: 0, y: -50 },
    to: { opacity: 1, y: 0 },
  },
  fadeLeft: {
    from: { opacity: 0, x: -50 },
    to: { opacity: 1, x: 0 },
  },
  fadeRight: {
    from: { opacity: 0, x: 50 },
    to: { opacity: 1, x: 0 },
  },
  slideUp: {
    from: { y: 100 },
    to: { y: 0 },
  },
  slideDown: {
    from: { y: -100 },
    to: { y: 0 },
  },
  slideLeft: {
    from: { x: -100 },
    to: { x: 0 },
  },
  slideRight: {
    from: { x: 100 },
    to: { x: 0 },
  },
  scale: {
    from: { scale: 0.8, opacity: 0 },
    to: { scale: 1, opacity: 1 },
  },
  scaleDown: {
    from: { scale: 1.2, opacity: 0 },
    to: { scale: 1, opacity: 1 },
  },
};

/**
 * Initialize scroll animations
 * Finds all elements with [data-scroll-animate] and applies animations
 */
export function initScrollAnimations() {
  const elements = document.querySelectorAll("[data-scroll-animate]");

  if (elements.length === 0) {
    return; // No elements to animate
  }

  elements.forEach((el) => {
    const animationType = el.getAttribute("data-scroll-animate");
    const duration = parseFloat(el.getAttribute("data-animate-duration")) || 0.8;
    const scrub = el.getAttribute("data-animate-scrub") === "true";
    const delay = parseFloat(el.getAttribute("data-animate-delay")) || 0;
    const ease = el.getAttribute("data-animate-ease") || "power2.out";
    const start = el.getAttribute("data-animate-start") || "top 80%";
    const stagger = parseFloat(el.getAttribute("data-animate-stagger")) || null;

    // Get animation preset
    const preset = ANIMATION_PRESETS[animationType];

    if (!preset) {
      console.warn(`Unknown animation type: ${animationType}`);
      return;
    }

    // Determine target elements (element itself or children if stagger is used)
    const targets = stagger
      ? Array.from(el.children).filter((child) => child.nodeType === 1)
      : el;

    // Set initial state
    gsap.set(targets, preset.from);

    // Create animation config
    const animationConfig = {
      ...preset.to,
      duration,
      delay,
      ease,
      force3D: true, // GPU acceleration
    };

    // Add stagger if specified
    if (stagger && targets.length > 1) {
      animationConfig.stagger = stagger;
    }

    // Create ScrollTrigger config
    const scrollTriggerConfig = {
      trigger: el,
      start: start,
      // markers: true, // Uncomment for debugging
    };

    // Add scrub if enabled
    if (scrub) {
      scrollTriggerConfig.scrub = true;
      scrollTriggerConfig.end = "bottom 20%";
    } else {
      scrollTriggerConfig.once = true; // Only animate once
    }

    // Apply animation
    gsap.to(targets, {
      ...animationConfig,
      scrollTrigger: scrollTriggerConfig,
    });
  });
}

/**
 * Cleanup function to destroy ScrollTrigger instances
 * Useful for SPA navigation or dynamic content updates
 */
export function destroyScrollAnimations() {
  ScrollTrigger.getAll().forEach((trigger) => {
    if (
      trigger.vars.trigger?.hasAttribute &&
      trigger.vars.trigger.hasAttribute("data-scroll-animate")
    ) {
      trigger.kill();
    }
  });
}

/**
 * Refresh ScrollTriggers
 * Useful after content changes or layout shifts
 */
export function refreshScrollAnimations() {
  ScrollTrigger.refresh();
}

export const scrollAnimations = {
  init: initScrollAnimations,
  destroy: destroyScrollAnimations,
  refresh: refreshScrollAnimations,
};
